import { useEffect, useRef, useState } from 'react';
import { TIMER_STATUS, EXERCISE_MODE } from '../../../../../types/enum';
import countdownBeepAudio from '../../../../../assets/audio/countdown_beep.mp3';
import startBeepAudio from '../../../../../assets/audio/start_beep.mp3';
import startAudio from '../../../../../assets/audio/start.mp3';
import workoutDoneAudio from '../../../../../assets/audio/workout_done.mp3';
import Controller from './controller';
import Timer from './timer';
import Display from './display';
import CompleteDialog from '../complete';
import Title from '../../../../../components/dialog/title';
import request from '../../../../../lib/request';

const audioSound = {
  countdownBeep: countdownBeepAudio,
  startBeep: startBeepAudio,
  start: startAudio,
  workoutDone: workoutDoneAudio,
};

let setAudioSound = false;

const getViewTimeFormat = (secNum) => {
  const minutes = String(Math.floor(secNum / 60)).padStart(2, '0');
  const seconds = String(secNum % 60).padStart(2, '0');

  return `${minutes}:${seconds}`;
};

const countdown3Seconds = (time) => {
  if (time === 3 || time === 2 || time === 1) {
    (async () => {
      await audioSound.countdownBeep.play();
    })();
  }
};

const zeroToStartBeep = (time) => {
  if (time === 0) {
    (async () => {
      await audioSound.startBeep.play();
    })();
  }
};

const zeroToStart = (time) => {
  if (time === 0) {
    (async () => {
      await audioSound.start.play();
    })();
  }
};

const workoutDone = () => {
  (async () => {
    await audioSound.workoutDone.play();
  })();
};

const CurrentState = (status, pausedAt) => {
  return {
    isPrepOrReadyOrDone:
      status === TIMER_STATUS.PREP ||
      status === TIMER_STATUS.READY ||
      status === TIMER_STATUS.DONE,
    isReadyOrDone:
      status === TIMER_STATUS.READY || status === TIMER_STATUS.DONE,
    isRestOrSetRestOrCycleRest:
      status === TIMER_STATUS.REST ||
      status === TIMER_STATUS.SET_REST ||
      status === TIMER_STATUS.CYCLE_REST,
    isReady: status === TIMER_STATUS.READY,
    isPrep: status === TIMER_STATUS.PREP,
    isExercise:
      status === TIMER_STATUS.EXERCISE ||
      (status === TIMER_STATUS.PAUSE && pausedAt === TIMER_STATUS.EXERCISE),
    isRest:
      status === TIMER_STATUS.REST ||
      (status === TIMER_STATUS.PAUSE && pausedAt === TIMER_STATUS.REST),
    isSetRest:
      status === TIMER_STATUS.SET_REST ||
      (status === TIMER_STATUS.PAUSE && pausedAt === TIMER_STATUS.SET_REST),
    isCycleRest:
      status === TIMER_STATUS.CYCLE_REST ||
      (status === TIMER_STATUS.PAUSE && pausedAt === TIMER_STATUS.CYCLE_REST),
    isPause: status === TIMER_STATUS.PAUSE,
    isDone: status === TIMER_STATUS.DONE,
  };
};

const ExerciseProgress = ({ doneExerciseCount, totalExerciseCount }) => {
  const arr = new Array(totalExerciseCount).fill(0);
  return (
    <div className='flex justify-center gap-[2px]'>
      {arr.map((_, index) => (
        <div
          key={index}
          className={`h-1 flex-1 rounded-xl ${
            doneExerciseCount > index ? 'bg-black' : 'bg-gray-200'
          }`}
        ></div>
      ))}
    </div>
  );
};

const Routine = ({
  dialogRef,
  closeDialog,
  selectedRoutine,
  open,
  refreshLog,
}) => {
  const repsIntervalRef = useRef(null);
  const timeIntervalRef = useRef(null);
  const exerciseValueRef = useRef(null);
  const restValueRef = useRef(null);
  const setRestValueRef = useRef(null);
  const cycleRestValueRef = useRef(null);
  const completeDialogRef = useRef(null);

  const [status, setStatus] = useState(TIMER_STATUS.READY);
  const [pausedAt, setPausedAt] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [isCompleteDialogOpen, setIsCompleteDialogOpen] = useState(false);

  const [runPrepTime, setRunPrepTime] = useState(15);
  const [runExerciseTime, setRunExerciseTime] = useState(0);
  const [runExerciseReps, setRunExerciseReps] = useState(0);
  const [runRestTime, setRunRestTime] = useState(0);
  const [runSetRestTime, setRunSetRestTime] = useState(0);
  const [runCycleRestTime, setRunCycleRestTime] = useState(0);
  const [totalRanTime, setTotalRanTime] = useState(0);

  const [cycle, setCycle] = useState(1);
  const [routine, setRoutine] = useState(null);
  const [currentSet, setCurrentSet] = useState(null);
  const [currentExercise, setCurrentExercise] = useState(null);
  const [currentGroupIndex, setCurrentGroupIndex] = useState(0);
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const [totalExerciseCount, setTotalExerciseCount] = useState(0);
  const [doneExerciseCount, setDoneExerciseCount] = useState(0);

  const [cycleExerciseCount, setCycleExerciseCount] = useState(1);
  const [totalCycleExerciseCount, setTotalCycleExerciseCount] = useState(0);

  const [nonCycleSetCount, setNonCycleSetCount] = useState(1);
  const [totalNonCycleExerciseCount, setTotalNonCycleExerciseCount] = useState(
    []
  );

  useEffect(() => {
    if (open) {
      getRoutine();
    } else {
      setRoutine(null);
    }
  }, [open]);

  const initialize = (routine) => {
    if (!routine) {
      return;
    }

    setStatus(TIMER_STATUS.READY);
    setCurrentGroupIndex(0);
    setCurrentExerciseIndex(0);
    setCurrentSet(routine.sets[0]);
    setCurrentExercise(routine.sets[0].exercises[0]);
    setDoneExerciseCount(0);
    setTotalExerciseCount(
      routine.sets.reduce((acc, cur) => {
        return acc + cur.exercises.length;
      }, 0) * routine.cycle
    );
    setTotalCycleExerciseCount(
      routine.sets.reduce((acc, cur) => {
        return acc + cur.exercises.length;
      }, 0)
    );

    setRunPrepTime(15);
    setRunRestTime(routine.sets[0].exercises[0]?.restTime ?? 0);
    setRunSetRestTime(routine.sets[0]?.restTime ?? 0);

    const initialExerciseMode = routine.sets[0].exercises[0].mode;
    const modeValue = routine.sets[0].exercises[0].modeValue;
    if (initialExerciseMode === EXERCISE_MODE.TIME) {
      setRunExerciseTime(modeValue);
    }
    if (initialExerciseMode === EXERCISE_MODE.REPS) {
      setRunExerciseReps(modeValue);
    }

    setAudioSetting();
  };

  const getRoutine = async () => {
    try {
      const { result } = await request.get(
        `/program/routine/${selectedRoutine.id}`
      );
      if (result) {
        setRoutine(result.routine);
        initialize(result.routine);

        if (!result.routine.cycleRoutine) {
          const setList = [];
          result.routine.sets.reduce((acc, cur, i, arr) => {
            if (i === 0) {
              acc.setId = cur.setId;
              acc.sets = 1;
              acc.exercises = cur.exercises.length;
              return acc;
            }

            if (i === arr.length - 1) {
              if (acc.setId === cur.setId) {
                acc.sets++;
                for (let count = 0; count < acc.sets; count++) {
                  setList.push(acc);
                }
              } else {
                const last = {
                  setId: cur.setId,
                  sets: 1,
                  exercises: cur.exercises.length,
                };
                setList.push(acc, last);
              }

              return {};
            }

            if (acc.setId === cur.setId) {
              acc.sets++;
              return acc;
            }

            for (let count = 0; count < acc.sets; count++) {
              setList.push(acc);
            }

            return {
              setId: cur.setId,
              sets: 1,
              exercises: cur.exercises.length,
            };
          }, {});
          setTotalNonCycleExerciseCount(setList);
        }
      }
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    const currentStatus = CurrentState(status, pausedAt);

    if (currentStatus.isPrep) {
      prepStart();
    }
    if (currentStatus.isExercise) {
      const { mode } = currentExercise;
      if (mode === EXERCISE_MODE.TIME) {
        exerciseTimerRun();
      }
      if (mode === EXERCISE_MODE.REPS) {
        exerciseRepsRun();
      }
    }
    if (currentStatus.isRest) {
      restTimerRun();
    }
    if (currentStatus.isSetRest) {
      setRestTimerRun();
    }
    if (currentStatus.isCycleRest) {
      cycleRestTimeRun();
    }
    if (currentStatus.isPause) {
      handlePause();
    }

    return () => {
      handlePause();
    };
  }, [status]);

  const handlePlay = () => {
    if (isPlaying) {
      setIsPlaying(false);
      setPausedAt(status);
      setStatus(TIMER_STATUS.PAUSE);
    }
    if (!isPlaying) {
      setIsPlaying(true);

      if (pausedAt) {
        setStatus(pausedAt);
        setPausedAt('');
      }

      const currentStatus = CurrentState(status, pausedAt);
      if (currentStatus.isReady && !pausedAt) {
        setStatus(TIMER_STATUS.PREP);
      }
    }
  };

  const handlePause = () => {
    clearInterval(timeIntervalRef.current);
    clearInterval(repsIntervalRef.current);
  };

  const prepStart = () => {
    // console.log('prep started');
    let tickingTime = runPrepTime;

    timeIntervalRef.current = setInterval(() => {
      tickingTime--;
      setRunPrepTime(tickingTime);

      countdown3Seconds(tickingTime);
      zeroToStart(tickingTime);

      if (tickingTime === -1) {
        clearInterval(timeIntervalRef.current);
        setStatus(TIMER_STATUS.EXERCISE);
      } else {
        setTotalRanTime((prev) => prev + 1);
      }
    }, 1000);
  };

  const exerciseTimerRun = () => {
    // console.log('exercise timer');
    exerciseValueRef.current = runExerciseTime;

    timeIntervalRef.current = setInterval(() => {
      exerciseValueRef.current--;
      setRunExerciseTime(exerciseValueRef.current);

      countdown3Seconds(exerciseValueRef.current);
      zeroToStartBeep(exerciseValueRef.current);

      if (exerciseValueRef.current === -1) {
        clearInterval(timeIntervalRef.current);
        setDoneExerciseCount((prev) => prev + 1);

        const lastIndexOfExercise = currentSet.exercises.length - 1;

        if (currentExerciseIndex < lastIndexOfExercise) {
          setRunRestTime(currentExercise.restTime);
          setStatus(TIMER_STATUS.REST);
        } else if (
          currentExerciseIndex === lastIndexOfExercise &&
          currentGroupIndex < routine.sets.length - 1
        ) {
          setRunSetRestTime(currentSet.restTime);
          setStatus(TIMER_STATUS.SET_REST);
        } else if (cycle < routine.cycle) {
          setRunCycleRestTime(routine.restTime);
          setStatus(TIMER_STATUS.CYCLE_REST);
        } else {
          workoutDone();
          setStatus(TIMER_STATUS.DONE);
          setIsPlaying(false);
          openCompleteDialog();
        }
      } else {
        setTotalRanTime((prev) => prev + 1);
      }
    }, 1000);
  };

  const exerciseRepsRun = () => {
    // console.log('exercise reps');
    repsIntervalRef.current = setInterval(() => {
      setTotalRanTime((prev) => prev + 1);
    }, 1000);
  };

  const restTimerRun = () => {
    // console.log('rest timer');
    restValueRef.current = runRestTime;

    timeIntervalRef.current = setInterval(() => {
      restValueRef.current--;
      setRunRestTime(restValueRef.current);

      countdown3Seconds(restValueRef.current);
      zeroToStartBeep(restValueRef.current);

      if (restValueRef.current === -1) {
        clearInterval(timeIntervalRef.current);

        if (currentExerciseIndex < currentSet.exercises.length - 1) {
          const nextExercise = currentSet.exercises[currentExerciseIndex + 1];
          if (nextExercise.mode === EXERCISE_MODE.TIME) {
            setRunExerciseTime(nextExercise.modeValue);
          }
          if (nextExercise.mode === EXERCISE_MODE.REPS) {
            setRunExerciseReps(nextExercise.modeValue);
          }
          setCurrentExercise(nextExercise);
          setCurrentExerciseIndex((prev) => prev + 1);
          setCycleExerciseCount((prev) => prev + 1);
          setStatus(TIMER_STATUS.EXERCISE);
        }
      } else {
        setTotalRanTime((prev) => prev + 1);
      }
    }, 1000);
  };

  const setRestTimerRun = () => {
    // console.log('set rest timer');
    setRestValueRef.current = runSetRestTime;

    timeIntervalRef.current = setInterval(() => {
      setRestValueRef.current--;
      setRunSetRestTime(setRestValueRef.current);

      countdown3Seconds(setRestValueRef.current);
      if (currentGroupIndex < routine.sets.length - 1) {
        zeroToStartBeep(setRestValueRef.current);
      }

      if (setRestValueRef.current === -1) {
        clearInterval(timeIntervalRef.current);

        if (currentGroupIndex < routine.sets.length - 1) {
          const nextSet = routine.sets[currentGroupIndex + 1];
          const firstExercise = nextSet.exercises[0];
          setCurrentSet(nextSet);
          setCurrentGroupIndex((prev) => prev + 1);

          if (!routine.cycleRoutine) {
            if (
              totalNonCycleExerciseCount[currentGroupIndex].sets <=
              nonCycleSetCount
            ) {
              setNonCycleSetCount(1);
            } else {
              setNonCycleSetCount((prev) => prev + 1);
            }
          }

          if (firstExercise.mode === EXERCISE_MODE.TIME) {
            setRunExerciseTime(firstExercise.modeValue);
          }
          if (firstExercise.mode === EXERCISE_MODE.REPS) {
            setRunExerciseReps(firstExercise.modeValue);
          }
          setCurrentExercise(firstExercise);
          setCurrentExerciseIndex(0);
          setCycleExerciseCount((prev) => prev + 1);
          setStatus(TIMER_STATUS.EXERCISE);
        }
      } else {
        setTotalRanTime((prev) => prev + 1);
      }
    }, 1000);
  };

  const cycleRestTimeRun = () => {
    // console.log('cycle rest timer');
    cycleRestValueRef.current = runCycleRestTime;

    timeIntervalRef.current = setInterval(() => {
      cycleRestValueRef.current--;
      setRunCycleRestTime(cycleRestValueRef.current);

      countdown3Seconds(cycleRestValueRef.current);
      if (cycle < routine.cycle) {
        zeroToStart(cycleRestValueRef.current);
      }

      if (cycleRestValueRef.current === -1) {
        clearInterval(timeIntervalRef.current);

        if (cycle < routine.cycle) {
          const firstSet = routine.sets[0];
          const firstExercise = firstSet.exercises[0];
          setCycle((prev) => prev + 1);
          setCurrentSet(firstSet);
          setCurrentExercise(firstExercise);
          if (firstExercise.mode === EXERCISE_MODE.TIME) {
            setRunExerciseTime(firstExercise.modeValue);
          }
          if (firstExercise.mode === EXERCISE_MODE.REPS) {
            setRunExerciseReps(firstExercise.modeValue);
          }
          setCurrentGroupIndex(0);
          setCurrentExerciseIndex(0);
          setCycleExerciseCount(1);
          setStatus(TIMER_STATUS.EXERCISE);
        }
      } else {
        setTotalRanTime((prev) => prev + 1);
      }
    }, 1000);
  };

  const handleRepsCheck = () => {
    if (repsIntervalRef?.current) {
      clearInterval(repsIntervalRef.current);
      zeroToStartBeep(0);
      setDoneExerciseCount((prev) => prev + 1);

      const lastIndexOfExercise = currentSet.exercises.length - 1;

      if (currentExerciseIndex < lastIndexOfExercise) {
        setIsPlaying(true);
        setRunRestTime(currentExercise.restTime);
        setStatus(TIMER_STATUS.REST);
      } else if (
        currentExerciseIndex === lastIndexOfExercise &&
        currentGroupIndex < routine.sets.length - 1
      ) {
        setIsPlaying(true);
        setRunSetRestTime(currentSet.restTime);
        setStatus(TIMER_STATUS.SET_REST);
      } else if (cycle < routine.cycle) {
        setIsPlaying(true);
        setRunCycleRestTime(routine.restTime);
        setStatus(TIMER_STATUS.CYCLE_REST);
      } else {
        workoutDone();
        setStatus(TIMER_STATUS.DONE);
        setIsPlaying(false);
        openCompleteDialog();
      }
    }
  };

  const handleAddRestTime = (time) => () => {
    const { isRest, isSetRest, isCycleRest } = CurrentState(status, pausedAt);

    if (isRest) {
      restValueRef.current += time;
      setRunRestTime(restValueRef.current);
    }
    if (isSetRest) {
      setRestValueRef.current += time;
      setRunSetRestTime(setRestValueRef.current);
    }
    if (isCycleRest) {
      cycleRestValueRef.current += time;
      setRunCycleRestTime(cycleRestValueRef.current);
    }
  };

  const handleNext = () => {
    const {
      isReadyOrDone,
      isPrep,
      isExercise,
      isRest,
      isSetRest,
      isCycleRest,
    } = CurrentState(status, pausedAt);

    if (isReadyOrDone) {
      return;
    }
    if (isPrep) {
      clearInterval(timeIntervalRef.current);
      setIsPlaying(true);
      setStatus(TIMER_STATUS.EXERCISE);
    }
    if (isExercise) {
      clearInterval(timeIntervalRef.current);
      clearInterval(repsIntervalRef.current);
      setDoneExerciseCount((prev) => prev + 1);

      const currentLastIndexOfExercise = currentSet.exercises.length - 1;
      const lastIndexOfGroup = routine.sets.length - 1;

      if (currentExerciseIndex < currentLastIndexOfExercise) {
        setIsPlaying(true);
        setRunRestTime(currentExercise.restTime);
        setStatus(TIMER_STATUS.REST);
      } else if (
        currentExerciseIndex === currentLastIndexOfExercise &&
        currentGroupIndex < lastIndexOfGroup
      ) {
        setIsPlaying(true);
        setRunSetRestTime(currentSet.restTime);
        setStatus(TIMER_STATUS.SET_REST);
      } else if (cycle < routine.cycle) {
        setIsPlaying(true);
        setRunCycleRestTime(routine.restTime);
        setStatus(TIMER_STATUS.CYCLE_REST);
      } else {
        workoutDone();
        setStatus(TIMER_STATUS.DONE);
        setIsPlaying(false);
        openCompleteDialog();
      }
    }
    if (isRest) {
      clearInterval(timeIntervalRef.current);

      setIsPlaying(true);
      const nextExercise = currentSet.exercises[currentExerciseIndex + 1];
      if (nextExercise.mode === EXERCISE_MODE.TIME) {
        setRunExerciseTime(nextExercise.modeValue);
      }
      if (nextExercise.mode === EXERCISE_MODE.REPS) {
        setRunExerciseReps(nextExercise.modeValue);
      }
      setCurrentExercise(nextExercise);
      setCurrentExerciseIndex((prev) => prev + 1);
      setCycleExerciseCount((prev) => prev + 1);
      setStatus(TIMER_STATUS.EXERCISE);
    }
    if (isSetRest) {
      clearInterval(timeIntervalRef.current);

      setIsPlaying(true);
      const nextSet = routine.sets[currentGroupIndex + 1];
      setCurrentSet(nextSet);

      if (!routine.cycleRoutine) {
        if (
          totalNonCycleExerciseCount[currentGroupIndex].sets <= nonCycleSetCount
        ) {
          setNonCycleSetCount(1);
        } else {
          setNonCycleSetCount((prev) => prev + 1);
        }
      }
      setCurrentGroupIndex((prev) => prev + 1);

      if (nextSet.exercises[0].mode === EXERCISE_MODE.TIME) {
        setRunExerciseTime(nextSet.exercises[0].modeValue);
      }
      if (nextSet.exercises[0].mode === EXERCISE_MODE.REPS) {
        setRunExerciseReps(nextSet.exercises[0].modeValue);
      }
      setCurrentExercise(nextSet.exercises[0]);
      setCurrentExerciseIndex(0);
      setCycleExerciseCount((prev) => prev + 1);
      setStatus(TIMER_STATUS.EXERCISE);
    }
    if (isCycleRest) {
      clearInterval(timeIntervalRef.current);

      setIsPlaying(true);
      const firstSet = routine.sets[0];
      const firstExercise = firstSet.exercises[0];
      setCycle((prev) => prev + 1);
      if (firstExercise.mode === EXERCISE_MODE.TIME) {
        setRunExerciseTime(firstExercise.modeValue);
      }
      if (firstExercise.mode === EXERCISE_MODE.REPS) {
        setRunExerciseReps(firstExercise.modeValue);
      }
      setCurrentSet(firstSet);
      setCurrentExercise(firstExercise);
      setCurrentGroupIndex(0);
      setCurrentExerciseIndex(0);
      setCycleExerciseCount(1);
      setStatus(TIMER_STATUS.EXERCISE);
    }
  };

  const handlePrev = () => {
    const {
      isPrepOrReadyOrDone,
      isExercise,
      isRest,
      isSetRest,
      isCycleRest,
      isRestOrSetRestOrCycleRest,
    } = CurrentState(status, pausedAt);

    if (isPrepOrReadyOrDone) {
      return;
    }
    if (isExercise) {
      clearInterval(timeIntervalRef.current);
      clearInterval(repsIntervalRef.current);
      setDoneExerciseCount((prev) => prev - 1);

      if (currentExerciseIndex > 0) {
        setIsPlaying(true);
        const prevExercise = currentSet.exercises[currentExerciseIndex - 1];
        setCurrentExercise(prevExercise);
        setCurrentExerciseIndex((prev) => prev - 1);
        setCycleExerciseCount((prev) => prev - 1);
        setRunRestTime(prevExercise.restTime);
        setStatus(TIMER_STATUS.REST);
      }
      if (currentExerciseIndex === 0) {
        if (currentGroupIndex > 0) {
          setIsPlaying(true);
          const prevSet = routine.sets[currentGroupIndex - 1];
          const lastIndexOfPrevSet = prevSet.exercises.length - 1;
          setCurrentExercise(prevSet.exercises[lastIndexOfPrevSet]);
          setCurrentExerciseIndex(lastIndexOfPrevSet);
          setCurrentSet(prevSet);

          if (!routine.cycleRoutine) {
            if (nonCycleSetCount === 1) {
              setNonCycleSetCount(
                totalNonCycleExerciseCount[currentGroupIndex - 1].sets
              );
            } else {
              setNonCycleSetCount((prev) => prev - 1);
            }
          }
          setCurrentGroupIndex((prev) => prev - 1);

          setCycleExerciseCount((prev) => prev - 1);
          setRunSetRestTime(prevSet.restTime);
          setStatus(TIMER_STATUS.SET_REST);
        }
        if (currentGroupIndex === 0 && cycle > 1) {
          setIsPlaying(true);
          const lastIndexOfGroup = routine.sets.length - 1;
          const lastPrevSet = routine.sets[lastIndexOfGroup];
          const lastIndexOfPrevSet = lastPrevSet.exercises.length - 1;
          setCurrentExercise(lastPrevSet.exercises[lastIndexOfPrevSet]);
          setCurrentExerciseIndex(lastIndexOfPrevSet);
          setCurrentSet(lastPrevSet);
          setCurrentGroupIndex(lastIndexOfGroup);
          setCycleExerciseCount(totalCycleExerciseCount);
          setCycle((prev) => prev - 1);
          setRunCycleRestTime(routine.restTime);
          setStatus(TIMER_STATUS.CYCLE_REST);
        }
      }
    }
    if (isRest) {
      clearInterval(restValueRef.current);
    }
    if (isSetRest) {
      clearInterval(setRestValueRef.current);
    }
    if (isCycleRest) {
      clearInterval(cycleRestValueRef.current);
    }
    if (isRestOrSetRestOrCycleRest) {
      setIsPlaying(true);
      const { mode, modeValue } = currentExercise;
      if (mode === EXERCISE_MODE.TIME) {
        setRunExerciseTime(modeValue);
      }
      if (mode === EXERCISE_MODE.REPS) {
        setRunExerciseReps(modeValue);
      }
      setStatus(TIMER_STATUS.EXERCISE);
    }
  };

  const handleClose = () => {
    if (
      !window.confirm(
        'If you close, not saved workout data will be lost.\nAre you sure you want to quit?'
      )
    ) {
      return;
    }

    setIsPlaying(false);
    resetState();
    closeDialog();
  };

  const resetState = () => {
    clearInterval(timeIntervalRef.current);
    clearInterval(repsIntervalRef.current);

    setStatus(TIMER_STATUS.READY);
    setPausedAt('');
    setIsPlaying(false);
    setIsCompleteDialogOpen(false);
    setRunPrepTime(15);
    setRunExerciseTime(0);
    setRunExerciseReps(0);
    setRunRestTime(0);
    setRunSetRestTime(0);
    setRunCycleRestTime(0);
    setTotalRanTime(0);
    setCycle(1);
    setRoutine(null);
    setCurrentSet(null);
    setCurrentExercise(null);
    setCurrentGroupIndex(0);
    setNonCycleSetCount(1);
    setCurrentExerciseIndex(0);
    setCycleExerciseCount(1);
    setTotalExerciseCount(0);
    setDoneExerciseCount(0);
  };

  const setAudioSetting = () => {
    if (!setAudioSound) {
      for (let [key, audio] of Object.entries(audioSound)) {
        const sound = new Audio(audio);
        audioSound[key] = sound;
      }
      setAudioSound = true;
    }
  };

  const openCompleteDialog = () => {
    if (completeDialogRef.current) {
      completeDialogRef.current.showModal();
      setIsCompleteDialogOpen(true);
    }
  };

  const closeCompleteDialog = () => {
    if (completeDialogRef.current) {
      completeDialogRef.current.close();
      setIsCompleteDialogOpen(false);
    }
  };

  return (
    <dialog
      ref={dialogRef}
      className='w-full h-full max-w-lg max-h-full p-4'
      onCancel={(e) => {
        e.preventDefault();
      }}
    >
      <Title
        title={routine?.title}
        onClick={handleClose}
      />
      <div className='my-4'>
        <ExerciseProgress
          doneExerciseCount={doneExerciseCount}
          totalExerciseCount={totalExerciseCount}
        />
      </div>
      <div className='h-[calc(100%-68px)]'>
        <Display
          status={status}
          getViewTimeFormat={getViewTimeFormat}
          totalRanTime={totalRanTime}
          cycle={cycle}
          routine={routine}
          currentExercise={currentExercise}
          currentSet={currentSet}
          currentExerciseIndex={currentExerciseIndex}
          currentGroupIndex={currentGroupIndex}
          cycleExerciseCount={cycleExerciseCount}
          totalCycleExerciseCount={totalCycleExerciseCount}
          nonCycleSetCount={nonCycleSetCount}
          totalNonCycleExerciseCount={totalNonCycleExerciseCount}
        />
        <Timer
          status={status}
          pausedAt={pausedAt}
          handleAddRestTime={handleAddRestTime}
          getViewTimeFormat={getViewTimeFormat}
          runPrepTime={runPrepTime}
          runExerciseTime={runExerciseTime}
          runExerciseReps={runExerciseReps}
          runRestTime={runRestTime}
          runSetRestTime={runSetRestTime}
          runCycleRestTime={runCycleRestTime}
          totalRanTime={totalRanTime}
          currentExercise={currentExercise}
        />
        <Controller
          handlePrev={handlePrev}
          status={status}
          pausedAt={pausedAt}
          handleRepsCheck={handleRepsCheck}
          isPlaying={isPlaying}
          handlePlay={handlePlay}
          handleNext={handleNext}
          currentExercise={currentExercise}
        />
      </div>
      <CompleteDialog
        completeDialogRef={completeDialogRef}
        closeDialog={closeCompleteDialog}
        isDialogOpen={isCompleteDialogOpen}
        workoutTime={totalRanTime}
        closeWorkoutDialog={() => {
          closeDialog();
          refreshLog();
        }}
        routine={routine}
        selectedDay={routine?.date}
      />
    </dialog>
  );
};

export default Routine;
